import type { Node, Root } from "interfaces/schemas";
import { NodeSchema } from "interfaces/schemas";
import { NodeKinds } from "interfaces/enums";
import { toast } from "react-toastify";
import type { ProjectNodes } from "stores/project/project/projectStore";

export const buildTree = (
  nodes: ProjectNodes,
  id: string,
  title: string,
): Node[] | null => {
  try {
    if (!nodes || !Array.isArray(nodes)) {
      throw new Error("Invalid nodes data");
    }
    const map = new Map<string | null, Node[]>();
    const rootNodes: Node[] = [];

    // Pre-sort nodes by position to avoid sorting multiple times
    const sortedNodes = [...nodes].sort(
      (a, b) => (a?.position ?? 0) - (b?.position ?? 0),
    );

    for (const node of sortedNodes) {
      const parsedNode = NodeSchema.parse(node);
      const siblings = map.get(parsedNode.parentId) ?? [];
      siblings.push(parsedNode);
      map.set(parsedNode.parentId, siblings);

      if (parsedNode.parentId === null) {
        rootNodes.push({
          ...parsedNode,
          parentId: id,
        });
      }
    }

    composeChildren(rootNodes, map);

    const tree: Root = {
      kind: NodeKinds.ROOT,
      id,
      parentId: null,
      position: 0,
      folderName: title,
      nodes: rootNodes,
      folderDescription: null,
      active: true,
      partiallyVisible: false,
    };

    return [tree];
  } catch (error) {
    // Log the error for debugging
    console.error("Failed to build tree:", error);

    // Show error to user
    toast.error("Failed to build tree");

    return null;
  }
};

const composeChildren = (
  nodes: Node[],
  map: Map<string | null, Node[]>,
): void => {
  for (const node of nodes) {
    const children = map.get(node.id);
    if (children && Array.isArray(children) && children.length > 0) {
      node.nodes = children;
      composeChildren(node.nodes, map);
    }
  }
};
